/**
 * Verjaardagslogica op het datumdeel van de geboortedatum-string (string-parsing,
 * bewust geen Date-constructie vanuit de volledige ISO-string — geen
 * tijdzone-verschuiving). Geef `birthDateLocal` (kloktijd op de geboorteplaats)
 * mee: de verjaardag is de kalenderdag dáár, niet de UTC-dag (die kan een dag
 * vroeger vallen, bug 2026-10-20); `birthDateUtc` is enkel de terugval. Zelfde regels als de backend (`SelectUpcoming`):
 * 29 februari telt in niet-schrikkeljaren als 1 maart.
 */

function parseBirthParts(birthDate: string): { year: number; month: number; day: number } | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(birthDate);
  if (!match) return null;
  return { year: Number(match[1]), month: Number(match[2]), day: Number(match[3]) };
}

function isLeapYear(year: number): boolean {
  return (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0;
}

function birthdayInYear(month: number, day: number, year: number): Date {
  if (month === 2 && day === 29 && !isLeapYear(year)) return new Date(year, 2, 1);
  return new Date(year, month - 1, day);
}

/** Eerstvolgende verjaardag (lokale datum, zonder tijd) + de leeftijd die dan bereikt wordt. */
export function nextBirthday(
  birthDate: string | null | undefined,
  today: Date = new Date(),
): { date: Date; turnsAge: number } | null {
  if (!birthDate) return null;
  const parts = parseBirthParts(birthDate);
  if (!parts) return null;

  const todayDate = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  let next = birthdayInYear(parts.month, parts.day, todayDate.getFullYear());
  if (next < todayDate) next = birthdayInYear(parts.month, parts.day, todayDate.getFullYear() + 1);

  return { date: next, turnsAge: next.getFullYear() - parts.year };
}

/** Valt de eerstvolgende verjaardag binnen `daysAhead` dagen (vandaag meegeteld)? */
export function isBirthdayWithinDays(
  birthDate: string | null | undefined,
  daysAhead: number,
  today: Date = new Date(),
): boolean {
  const upcoming = nextBirthday(birthDate, today);
  if (!upcoming) return false;
  const todayDate = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  const diffDays = (upcoming.date.getTime() - todayDate.getTime()) / 86_400_000;
  return diffDays <= daysAhead;
}
